import React, { FC, useEffect, useState } from 'react';
import { UseQueryOptions } from 'react-query';
import { Route, useParams } from 'react-router-dom';
import { Grid, Link } from '@material-ui/core';
import { StreamChat } from 'stream-chat';
import { Chat } from 'stream-chat-react';

import { ContentApproval, Content, GetContentQuery, useGetContentQuery } from 'api';
import { ContentProvider, DimensionsProvider, QueryVariablesProvider } from 'providers';
import { useCurrentUser, useMonitorPost, useProtectedClient } from 'hooks';
import { Dimensions, Layouts, ErrorPage, QueryManager, EmptyState } from 'components';
import { InlineChannel } from 'features/discussions';
import { Events } from 'analytics';
import { GETSTREAM_API_KEY, IMGIX_HOST } from 'settings';
import { getRandomProfileImage } from 'helpers';
import { StreamChatGenerics } from 'features/discussions/types';

import { getContentApprovalFromContent, getLayout, getVideo } from '../helpers';

interface IParams {
  content_id: string;
}

const REFETCH_INTERVAL = 1000 * 5;

const Permalink: FC = () => {
  const { content_id } = useParams<IParams>();
  const { state, dispatch } = useMonitorPost();
  const client = useProtectedClient();
  const currentUser = useCurrentUser();
  const [chatClient, setChatClient] = useState(null);

  const queryOptions: UseQueryOptions<GetContentQuery> = !state.has(content_id)
    ? {}
    : {
        refetchInterval: REFETCH_INTERVAL,
        isDataEqual: (oldData, newData) => {
          const video = getVideo(newData.getContent);
          if (video?.status === 'complete') {
            dispatch({ type: 'unwatch', id: content_id });
          }
          return false;
        },
      };

  const getContentResult = useGetContentQuery({ content_id, display_only: true }, queryOptions);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('es_source')) {
      window?.analytics?.track(Events.RepostClicked, {
        referral_source: searchParams.get('es_source'),
      });
    }

    void setupStreamChat();
  }, []);

  async function setupStreamChat() {
    const imageUrl = currentUser.image
      ? `${IMGIX_HOST}/${currentUser.image.key}?auto=format,compress`
      : getRandomProfileImage().src;

    const streamChatClient = StreamChat.getInstance<StreamChatGenerics>(GETSTREAM_API_KEY);
    const streamChatUser = { id: currentUser.id, name: currentUser.full_name, image: imageUrl };

    await streamChatClient.connectUser(streamChatUser, currentUser.getstream_token);

    setChatClient(streamChatClient);
  }

  function renderContentDeleted() {
    return (
      <EmptyState
        name="contentDeleted"
        actionComponent={
          <Link target="_blank" href={'mailto:support@everyonesocial.com'}>
            {'support@everyonesocial.com'}
          </Link>
        }
      />
    );
  }

  function renderContent() {
    const { data } = getContentResult;
    const { getContent } = data;

    const ContentComponent = getLayout(getContent);

    return (
      <QueryVariablesProvider value={{ content_id, display_only: true }}>
        <ContentProvider value={getContent}>
          <Dimensions>
            {dimensions => (
              <DimensionsProvider value={dimensions}>
                <ContentComponent />
              </DimensionsProvider>
            )}
          </Dimensions>
        </ContentProvider>
      </QueryVariablesProvider>
    );
  }

  function renderLegacy() {
    return (
      <QueryManager {...getContentResult}>
        {({ data }) => {
          if (!data?.getContent) {
            return <Route component={() => <ErrorPage type="not-found" />} />;
          }
          if (
            getContentApprovalFromContent(data?.getContent as Content) == ContentApproval.Deleted
          ) {
            return renderContentDeleted();
          }
          return renderContent();
        }}
      </QueryManager>
    );
  }

  function renderDiscussionsLayout() {
    return (
      <QueryManager {...getContentResult}>
        {({ data }) => {
          if (!data?.getContent) {
            return <Route component={ErrorPage} />;
          }

          if (
            getContentApprovalFromContent(data?.getContent as Content) == ContentApproval.Deleted
          ) {
            return renderContentDeleted();
          }

          return (
            <QueryVariablesProvider value={{ content_id }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  {renderContent()}
                </Grid>
                <Grid item xs={12} md={6}>
                  {!!chatClient && (
                    <Chat client={chatClient}>
                      <InlineChannel id={data.getContent.content_id} />
                    </Chat>
                  )}
                </Grid>
              </Grid>
            </QueryVariablesProvider>
          );
        }}
      </QueryManager>
    );
  }

  if (client.disable_comments) {
    return (
      <Layouts.App1Column
        navigation
        desktopSize={6}
        main={<Layouts.AppMain1Column main={renderLegacy()} />}
      />
    );
  }

  return (
    <Layouts.App1Column
      navigation
      desktopSize={12}
      main={<Layouts.AppMain1Column main={renderDiscussionsLayout()} />}
    />
  );
};

export default Permalink;
